import {
  all,
  call,
  fork,
  put,
  select,
  takeEvery,
  cancelled,
  takeLatest,
} from "redux-saga/effects";
import { SagaIterator } from "redux-saga";
import { JsonApiResponse } from "@stemscopes/api";
import axios from "axios";
import { endpoints } from "configs";
import { get } from "lodash-es";
import { request, generatePath } from "utils";
import { Standard } from "types";
import * as actions from "./actions";
import { prepareStandards } from "./selectors";
import * as Types from "./types";

const STANDARD_FIELDS =
  "id,name,description,standardTypeName,displayTheme,standardBreadcrumbs,position,disabled,deleted";

function* fetchRootStandards(): SagaIterator {
  try {
    const response: JsonApiResponse<Types.FetchedStandard[]> = yield call(
      request.get,
      "/standards/roots",
      {
        params: {
          fields: {
            standard: STANDARD_FIELDS,
          },
        },
      },
    );

    yield put(
      actions.fetchRootStandards.success({
        data: response.data.data,
      }),
    );
  } catch {
    yield put(actions.fetchRootStandards.failure(undefined, undefined));
  }
}

function* fetchSingleStandard(id: string): SagaIterator {
  try {
    const response = yield call(request.get, `/standards/${id}`, {
      params: {
        fields: {
          standard: STANDARD_FIELDS,
        },
      },
    });
    yield put(actions.fetchSingleStandard({ standard: response.data.data }));
  } catch {
    // wait for implementing error components
  }
}

function* fetchStandards({
  payload: { search, id },
}: ReturnType<typeof actions.fetchStandards.request>): SagaIterator {
  const source = axios.CancelToken.source();
  try {
    const standard = yield select(
      (state) => state.standards.byId[id as string],
    );
    if (!standard && id) {
      yield fork(fetchSingleStandard, id);
    }

    const searchFilters = search
      ? {
          name: {
            value: search,
            operator: "contains",
          },
          description: {
            value: search,
            operator: "contains",
          },
          "standardType.name": {
            value: search,
            operator: "contains",
          },
        }
      : {};

    const response: JsonApiResponse<Types.FetchedStandard[]> = yield call(
      request.get,
      id ? `/standards/${id}/descendants` : "/standards",
      {
        params: {
          filter: {
            ...searchFilters,
          },
          filterLogic: "or",
          fields: {
            standard: STANDARD_FIELDS,
          },
          page: { number: 1, size: 50 },
        },
        cancelToken: source.token,
      },
    );

    yield put(
      actions.fetchStandards.success({
        data: response.data.data,
        searchTotalCount: get(
          response,
          "data.meta.pagination.count",
          null,
        ) as number,
      }),
    );
  } catch (error) {
    yield put(actions.fetchStandards.failure(undefined, undefined));
  } finally {
    if (yield cancelled()) {
      source.cancel();
    }
  }
}

function* fetchStandardInfo(id: string): SagaIterator {
  try {
    const response: JsonApiResponse<
      Omit<Standard, "standards"> & { standards: Types.FetchedStandard[] }
    > = yield call(request.get, generatePath(endpoints.standard, { id }), {
      params: {
        include: "standards",
        fields: { standard: `${STANDARD_FIELDS},standards` },
        page: { number: 1, size: 50 },
      },
    });

    const standards = prepareStandards(response.data.data.standards);
    yield put(actions.fetchStandardChildren({ standards, id }));
  } catch {
    // wait for implementing error components
  }
}

function* fetchStandardElements(id: string): SagaIterator {
  const response: JsonApiResponse = yield call(
    request.get,
    generatePath(endpoints.standardElements, { id }),
    {
      params: {
        include: "scope,section",
        fields: {
          element: "id,name,title,audienceName,section,scope,scopeId,sectionId",
          scope: "id,name",
        },
      },
    },
  );

  return response.data.data;
}

function* fetchStandardElementParts(id: string): SagaIterator {
  const response: JsonApiResponse = yield call(
    request.get,
    generatePath(endpoints.standardElementParts, { id }),
    {
      params: {
        include: "element,element.scope",
        fields: {
          element: "id,name,title,audienceName,scope",
          scope: "id,name",
        },
      },
    },
  );

  return response.data.data;
}

function* fetchStandardScopes(id: string): SagaIterator {
  try {
    const [elements, elementParts] = yield all([
      yield call(fetchStandardElements, id),
      yield call(fetchStandardElementParts, id),
    ]);
    yield put(actions.fetchStandardScopes({ elements, elementParts, id }));
  } catch (e) {
    // wait for implementing error components
  }
}

function* fetchStandard({
  payload: { id },
}: ReturnType<typeof actions.fetchStandard>): SagaIterator {
  yield fork(fetchStandardInfo, id);
  yield fork(fetchStandardScopes, id);
}

export default function* rootSaga(): SagaIterator {
  yield all([
    takeEvery(actions.fetchRootStandards.request, fetchRootStandards),
    takeLatest(actions.fetchStandards.request, fetchStandards),
    takeEvery(actions.fetchStandard, fetchStandard),
  ]);
}
